import {
  EAgentToolType,
  EAiModelStatus,
  ECrewAiAgentMemoryMode,
  IAiAgentElkNodeData,
  IAiModel,
  ICrewAgentTool
} from "@kuika/kuika-cl-model";
import _ from "lodash";
import { ChangeEvent, KeyboardEvent as ReactKeyboardEvent, useEffect, useRef, useState } from "react";
import { Handle, NodeProps, Position } from "reactflow";
import { DatasourceService } from "../../../../services/datasource-service";
import KuikaDropdownMenu from "../../../ui-kit/kuika-dropdown-menu/kuika-dropdown-menu";
import KuikaSvg from "../../../ui-kit/kuika-svg/kuika-svg";
import KuikaTooltip from "../../../ui-kit/kuika-tooltip/kuika-tooltip";
import {
  AGENT_TOOLS,
  AI_AGENT_MANAGER_TARGET_HANDLE_PREFIX,
  CrewProcessType,
  IAgentToolOption
} from "./ai-agent-helper";
import AgentNodeDropdown from "./ai-agent-node-dropdown";

const formatModelLabel = (name: string): string => {
  // Convert model names like "gpt-4o" to "GPT-4o", "gemini-2.0-flash" to "Gemini 2.0 Flash"
  return name
    .split("-")
    .map((part, index) => {
      // Uppercase first part only if it's GPT
      if (index === 0) {
        if (part.toLowerCase() === "gpt") {
          return part.toUpperCase();
        }
        return part.charAt(0).toUpperCase() + part.slice(1).toLowerCase();
      }
      // Capitalize words like "flash", "mini", "lite", "pro", "nano"
      if (["flash", "mini", "lite", "pro", "nano"].includes(part.toLowerCase())) {
        return part.charAt(0).toUpperCase() + part.slice(1).toLowerCase();
      }
      // Keep version numbers as is
      return part;
    })
    .join(" ")
    .replace(/\s+/g, " ");
};

const formatAgentTitle = (name: string): string => {
  if (!name) {
    return "";
  }

  const spaced = name
    .replace(/[_-]+/g, " ")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/([A-Z])([A-Z][a-z])/g, "$1 $2")
    .replace(/\s+/g, " ")
    .trim();

  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
};

const getModelDisplayName = (model: any, llmModels: IAiModel[]): string => {
  if (!model) {
    return "";
  }

  if (typeof model === "string") {
    const modelById = llmModels.find((m) => m.id === model);
    if (modelById) {
      return formatModelLabel(modelById.name);
    }
    return formatModelLabel(model);
  }

  if (typeof model === "object") {
    if (model.name) {
      return formatModelLabel(model.name);
    }
    if (model.id) {
      const modelById = llmModels.find((m) => m.id === model.id);
      if (modelById) {
        return formatModelLabel(modelById.name);
      }
      return String(model.id);
    }
  }

  return "";
};

const getToolDefinition = (toolName: string) => {
  return AGENT_TOOLS.find((item) => item.name === toolName);
};

const truncateSelectionValue = (value: string, maxChars: number = 14): string => {
  if (!value) {
    return "";
  }

  return value.length > maxChars ? `${value.slice(0, maxChars)}...` : value;
};

// Extract the version number and size descriptor from a model name for the
// two-line badge on the circular model node: top line = version (e.g. "5.4",
// "2.5"), bottom line = the remaining size/variant words (e.g. "nano",
// "flash lite"). Full name stays in the hover tooltip.
// e.g. "gpt-5.4" -> { version: "5.4", size: "" }
//      "gpt-5.4-nano" -> { version: "5.4", size: "nano" }
//      "gemini-2.5-flash-lite" -> { version: "2.5", size: "flash lite" }
const getModelVersionBadge = (modelName: string): { version: string; size: string } => {
  if (!modelName) return { version: "", size: "" };
  const parts = modelName.split("-");
  const suffix = parts.slice(1);
  const versionIndex = suffix.findIndex((part) => /^\d/.test(part));
  if (versionIndex < 0) {
    return { version: suffix.join(" "), size: "" };
  }
  const version = suffix[versionIndex];
  const size = suffix.filter((_, i) => i !== versionIndex).join(" ");
  return { version, size };
};

export interface IAiAgentElkNodeEvents {
  updateTools: (tools: ICrewAgentTool[]) => void;
  updateName: (name: string) => void;
  updateMemory: (mode: ECrewAiAgentMemoryMode) => void;
  onAddAgentAfter: () => void;
  updateModel: (model: any) => void;
  onRemoveNode: () => void;
  onCopyNode: () => void;
  onOpenMcpConfig?: (toolIndex: number) => void;
  onToolClick?: () => void;
  // KOA-16736: KAction tools as separate appendage
  kActionOptions?: IAgentToolOption[];
  kActionTools?: ICrewAgentTool[];
  updateKActionTools?: (tools: ICrewAgentTool[]) => void;
  // Receives the clicked KAction's actionId so the parent can open its detail view.
  onActionClick?: (actionId: string | undefined) => void;
  readOnly?: boolean;
  processType?: CrewProcessType;
}

export default function ElkNode({
  id,
  data,
  selected,
  updateTools,
  updateName,
  updateMemory,
  onAddAgentAfter,
  updateModel,
  onRemoveNode,
  onCopyNode,
  onOpenMcpConfig,
  onToolClick,
  kActionOptions,
  kActionTools,
  updateKActionTools,
  onActionClick,
  readOnly = false,
  processType = "sequential"
}: NodeProps<IAiAgentElkNodeData> & IAiAgentElkNodeEvents) {
  const nameInputRef = useRef<HTMLInputElement>(null);
  const [llmModels, setLlmModels] = useState<IAiModel[]>([]);
  const [isLoadingModels, setIsLoadingModels] = useState(true);
  const [isNameOnEdit, setIsNameOnEdit] = useState(false);
  const [tempName, setTempName] = useState(data.metadata.name);
  const [contextMenuOpen, setContextMenuOpen] = useState(false);
  // KOA-16736: namespaced by appendage kind ("tools-0", "actions-0") so menus
  // on Tools and Actions appendages don't open simultaneously when indices match.
  const [openToolMenuKey, setOpenToolMenuKey] = useState<string | null>(null);

  useEffect(() => {
    const handleCloseAll = () => {
      setContextMenuOpen(false);
      setOpenToolMenuKey(null);
    };
    window.addEventListener("agent-dropdown-close-all", handleCloseAll);
    return () => window.removeEventListener("agent-dropdown-close-all", handleCloseAll);
  }, []);

  // Fetch LLM models on mount
  useEffect(() => {
    DatasourceService.GetAiModels()
      .then((response) => {
        // Parse model data to ensure ProviderId is properly set
        const enrichedModels = response.data.map((model: any) => ({
          ...model,
          providerId: model.providerId || model.ProviderId || null
        }));
        setLlmModels(enrichedModels);
      })
      .finally(() => setIsLoadingModels(false));
  }, []);

  // Update tempName when data changes
  useEffect(() => {
    setTempName(data.metadata.name);
  }, [data.metadata.name]);

  // Hide edit button when node is deselected
  useEffect(() => {
    if (!selected) {
      setIsNameOnEdit(false);
    }
  }, [selected]);

  // Input handlers
  const onNameChange = (event: ChangeEvent<HTMLInputElement>) => {
    setTempName(event.target.value);
  };

  const handleNameInputKeyDown = (event: ReactKeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      event.preventDefault();
      handleSubmitNameChange();
      return;
    }
    if (event.key === "Escape") {
      event.preventDefault();
      handleAbortNameChange();
    }
  };

  const handleSubmitNameChange = () => {
    const trimmed = tempName.trim();
    if (!trimmed) {
      return;
    }
    updateName(trimmed);
    setTempName(trimmed);
    setIsNameOnEdit(false);
  };

  const handleAbortNameChange = () => {
    setTempName(data.metadata.name);
    if (nameInputRef.current) {
      nameInputRef.current.value = data.metadata.name;
    }
    setIsNameOnEdit(false);
  };

  const handleAddTool = (tool: ICrewAgentTool) => {
    const tools = _.cloneDeep(data.metadata.tools) ?? [];
    tools.push(tool);
    updateTools(tools);
  };

  const handleToggleTool = (tool: ICrewAgentTool) => {
    const currentTools = _.cloneDeep(data.metadata.tools) ?? [];
    const isSelected = currentTools.some((item) => item.name === tool.name);

    if (isSelected) {
      updateTools(currentTools.filter((item) => item.name !== tool.name));
      return;
    }

    handleAddTool(tool);
  };

  const getToolsDropdownMenuOptions = () => {
    // KOA-16736: exclude KAction tools — those are managed by the Actions appendage
    const selectedToolNames = new Set(
      (data.metadata.tools ?? []).filter((tool) => tool.toolType !== EAgentToolType.KAction).map((tool) => tool.name)
    );

    return AGENT_TOOLS.map((tool) => {
      const isSelected = selectedToolNames.has(tool.name);

      return {
        group: tool.group,
        key: tool.name,
        label: tool.displayName || tool.name,
        value: tool.name,
        icon: isSelected ? (
          <KuikaSvg type="common" name="check" width={16} height={16} color="var(--green-6)" />
        ) : (
          <KuikaSvg
            type="agent-icons"
            name={tool.icon}
            width={12}
            height={12}
            useOriginalColors={!tool.icon.endsWith("-tool")}
          />
        ),
        onClick: () => {
          handleToggleTool(tool);
        }
      };
    });
  };

  // KOA-16736: KAction tools dropdown options and toggle handler
  const getActionsDropdownMenuOptions = () => {
    const selectedActionIds = new Set((kActionTools ?? []).map((t) => t.actionId));
    return (kActionOptions ?? []).map((action) => {
      const isSelected = selectedActionIds.has(action.actionId);
      return {
        group: action.group,
        key: `kaction-${action.actionId}`,
        label: action.displayName || action.name,
        value: action.actionId!,
        icon: isSelected ? (
          <KuikaSvg type="common" name="check" width={16} height={16} color="var(--green-6)" />
        ) : (
          <KuikaSvg type="datasource-icons" name="ai-action" width={12} height={12} />
        ),
        onClick: () => handleToggleKAction(action)
      };
    });
  };

  const handleToggleKAction = (action: IAgentToolOption) => {
    const current = kActionTools ?? [];
    const existing = current.find((t) => t.actionId === action.actionId);
    if (existing) {
      updateKActionTools?.(current.filter((t) => t.actionId !== action.actionId));
    } else {
      const newTool: ICrewAgentTool = {
        name: action.name,
        toolType: EAgentToolType.KAction,
        actionId: action.actionId,
        description: action.description
      };
      updateKActionTools?.([...current, newTool]);
    }
  };

  const agentNodeTitleWrapper = (name: string) => {
    return renderAgentName();
  };

  const renderAgentName = () => {
    const formattedAgentTitle = formatAgentTitle(data.metadata.name);

    if (readOnly) {
      return (
        <>
          <span className="react-flow__node-elk__header-icon">
            <KuikaSvg type="datasource-icons" name="ai-agent" width={16} height={16} color="var(--purple-5)" />
          </span>
          <div className="react-flow__node-elk__title-display">
            <KuikaTooltip content={formattedAgentTitle}>
              <div className="react-flow__node-elk__title">{formattedAgentTitle}</div>
            </KuikaTooltip>
          </div>
        </>
      );
    }

    // EDIT mode
    if (isNameOnEdit) {
      return (
        <>
          <span className="react-flow__node-elk__header-icon">
            <KuikaSvg type="datasource-icons" name="brain" width={16} height={16} color="var(--purple-5)" />
          </span>
          <div className="react-flow__node-elk__title-display">
            <input
              ref={nameInputRef}
              className="react-flow__node-elk__title-input"
              placeholder="Agent Name"
              value={tempName}
              onChange={onNameChange}
              onDoubleClickCapture={(e) => e.stopPropagation()}
              onClick={(e) => e.stopPropagation()}
              onKeyDown={handleNameInputKeyDown}
              autoFocus
            />
          </div>
          <svg
            className="title-submit"
            onClick={(e) => {
              e.stopPropagation();
              handleSubmitNameChange();
            }}
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            style={{ cursor: "pointer" }}
          >
            <path
              d="M14.4903 2.99545C14.3604 2.99923 14.2371 3.0534 14.1465 3.14649L5.50004 11.793L1.85355 8.14649C1.80748 8.0985 1.7523 8.06019 1.69123 8.0338C1.63017 8.00741 1.56445 7.99347 1.49793 7.99279C1.43141 7.99211 1.36543 8.00472 1.30384 8.02986C1.24225 8.05501 1.1863 8.09219 1.13926 8.13923C1.09222 8.18626 1.05504 8.24222 1.0299 8.3038C1.00475 8.36539 0.992147 8.43138 0.992824 8.4979C0.9935 8.56442 1.00744 8.63013 1.03383 8.6912C1.06023 8.75226 1.09854 8.80745 1.14652 8.85352L5.14652 12.8535C5.24029 12.9473 5.36745 12.9999 5.50004 12.9999C5.63262 12.9999 5.75978 12.9473 5.85355 12.8535L14.8536 3.85352C14.9257 3.78327 14.9749 3.69288 14.9948 3.59417C15.0148 3.49547 15.0044 3.39306 14.9651 3.30034C14.9259 3.20761 14.8596 3.1289 14.7748 3.0745C14.6901 3.0201 14.5909 2.99255 14.4903 2.99545Z"
              fill="#246EFF"
              stroke="#246EFF"
            />
          </svg>
          <svg
            className="title-cancel"
            onClick={(e) => {
              e.stopPropagation();
              handleAbortNameChange();
            }}
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            style={{ cursor: "pointer" }}
          >
            <path
              d="M13.1621 2.32619C13.03 2.32931 12.9046 2.38454 12.8131 2.47984L7.99996 7.29299L3.1868 2.47984C3.1402 2.43184 3.08444 2.39368 3.02282 2.36763C2.9612 2.34157 2.89498 2.32815 2.82808 2.32814C2.72857 2.32817 2.63134 2.35788 2.54881 2.41348C2.46628 2.46907 2.40222 2.54803 2.36482 2.64024C2.32742 2.73245 2.31838 2.83372 2.33886 2.9311C2.35933 3.02848 2.4084 3.11753 2.47977 3.18687L7.29292 8.00002L2.47977 12.8132C2.43179 12.8592 2.39348 12.9144 2.36709 12.9755C2.34069 13.0366 2.32675 13.1023 2.32608 13.1688C2.3254 13.2353 2.338 13.3013 2.36315 13.3629C2.38829 13.4245 2.42547 13.4804 2.47251 13.5275C2.51955 13.5745 2.5755 13.6117 2.63709 13.6368C2.69868 13.662 2.76466 13.6746 2.83118 13.6739C2.8977 13.6732 2.96342 13.6593 3.02448 13.6329C3.08555 13.6065 3.14073 13.5682 3.1868 13.5202L7.99996 8.70705L12.8131 13.5202C12.8592 13.5682 12.9144 13.6065 12.9754 13.6329C13.0365 13.6593 13.1022 13.6732 13.1687 13.6739C13.2352 13.6746 13.3012 13.662 13.3628 13.6368C13.4244 13.6117 13.4804 13.5745 13.5274 13.5275C13.5744 13.4804 13.6116 13.4245 13.6368 13.3629C13.6619 13.3013 13.6745 13.2353 13.6738 13.1688C13.6732 13.1023 13.6592 13.0366 13.6328 12.9755C13.6064 12.9144 13.5681 12.8592 13.5201 12.8132L8.70699 8.00002L13.5201 3.18687C13.5928 3.11708 13.6428 3.02695 13.6634 2.9283C13.684 2.82965 13.6743 2.72707 13.6356 2.63402C13.5969 2.54097 13.5309 2.46179 13.4464 2.40686C13.3619 2.35193 13.2628 2.32381 13.1621 2.32619Z"
              fill="#818C99"
              stroke="#818C99"
            />
          </svg>
        </>
      );
    }

    // Show name with brain icon
    return (
      <>
        <span className="react-flow__node-elk__header-icon">
          <KuikaSvg type="datasource-icons" name="ai-agent" width={16} height={16} color="var(--purple-5)" />
        </span>
        <div className="react-flow__node-elk__title-display">
          <div className="react-flow__node-elk__title">{formattedAgentTitle}</div>
        </div>
      </>
    );
  };

  const isNoToolAvailable = () => {
    return AGENT_TOOLS.length === 0;
  };

  // Read-only meta cluster anchored to the top-right corner of the agent
  // node. Shows provider + model version/size, memory mode (non-hierarchical
  // only), and a guardrail shield when configured. Edits happen in the right
  // sidebar — these are purely informational badges.
  const renderAgentMeta = () => {
    // Model segment
    const modelRef = data.metadata.model;
    const modelKey = typeof modelRef === "object" ? (modelRef as any).id || (modelRef as any).name : modelRef;
    const assignedModel = modelKey ? llmModels.find((m) => m.id === modelKey || m.name === modelKey) : undefined;
    const providerIconName = assignedModel ? getProviderIcon(assignedModel) : "";
    const rawModelName = assignedModel?.name || (typeof modelKey === "string" ? modelKey : "");
    const versionBadge = getModelVersionBadge(rawModelName);
    const modelLoading = isLoadingModels && !!modelKey;

    const modelStatus = getAssignedModelStatus();
    const modelStatusClass =
      modelStatus === EAiModelStatus.Shutdown
        ? " react-flow__node-elk__meta-chip--shutdown"
        : modelStatus === EAiModelStatus.Deprecated
          ? " react-flow__node-elk__meta-chip--deprecated"
          : "";

    const modelFullLabel = assignedModel ? formatModelLabel(rawModelName) : "";
    const modelSegment = modelLoading ? (
      <span className="react-flow__node-elk__meta-chip react-flow__node-elk__meta-chip--muted">
        <span className="ai-agent-inline-spinner" aria-label="Loading model" />
      </span>
    ) : assignedModel ? (
      <KuikaTooltip content={modelFullLabel} position="top">
        <span className={`react-flow__node-elk__meta-chip${modelStatusClass}`}>
          {providerIconName ? (
            <KuikaSvg type="agent-icons" name={providerIconName} width={12} height={12} useOriginalColors />
          ) : (
            <span className="react-flow__node-elk__meta-chip-letter">
              {rawModelName.charAt(0).toUpperCase() || "?"}
            </span>
          )}
          <span className="react-flow__node-elk__meta-chip-version">{versionBadge.version || modelFullLabel}</span>
          {versionBadge.size && <span className="react-flow__node-elk__meta-chip-size">{versionBadge.size}</span>}
        </span>
      </KuikaTooltip>
    ) : (
      <KuikaTooltip content="No model selected" position="top">
        <span className="react-flow__node-elk__meta-chip react-flow__node-elk__meta-chip--muted">
          <KuikaSvg type="datasource-icons" name="brain" width={12} height={12} color="var(--dark-6)" />
          <span className="react-flow__node-elk__meta-chip-version">No model</span>
        </span>
      </KuikaTooltip>
    );

    // Memory segment (non-hierarchical only — manager owns memory in hierarchical)
    const memoryLabel =
      currentMemoryMode === ECrewAiAgentMemoryMode.Shared
        ? "Shared"
        : currentMemoryMode === ECrewAiAgentMemoryMode.Isolated
          ? "Isolated"
          : "Off";
    const memoryActive = currentMemoryMode !== ECrewAiAgentMemoryMode.Disabled;
    const memorySegment = !isHierarchical && memoryActive && (
      <KuikaTooltip content={`Memory: ${memoryLabel}`} position="top">
        <span className="react-flow__node-elk__meta-chip react-flow__node-elk__meta-chip--memory">
          <KuikaSvg type="datasource-icons" name="brain" width={12} height={12} color="var(--purple-5)" />
          <span className="react-flow__node-elk__meta-chip-version">{memoryLabel}</span>
        </span>
      </KuikaTooltip>
    );

    // Guardrail segment (only when configured)
    const guardrailSegment = hasGuardrail && (
      <KuikaTooltip content="Guardrails applied to this agent" position="top">
        <span className="react-flow__node-elk__meta-chip react-flow__node-elk__meta-chip--guardrail">
          <KuikaSvg name="shield-check" width={12} height={12} color="var(--green-6)" />
        </span>
      </KuikaTooltip>
    );

    return (
      <div className="react-flow__node-elk__meta" onMouseDown={stopNodePropagation} onClick={stopNodePropagation}>
        {modelSegment}
        {memorySegment}
        {guardrailSegment}
      </div>
    );
  };

  // Provider ID mapping for grouping models by provider
  const PROVIDER_NAMES: Record<string, string> = {
    "4D9AC0E3-D3EE-49D1-B0F6-813D9A7C237F": "OpenAI",
    "876905BF-0E2C-4D20-84D1-D2E3B5FAFDA1": "Google"
  };

  const getProviderName = (model: IAiModel): string => {
    // Access providerId from the enriched model data and normalize to uppercase for case-insensitive lookup
    const providerId = (model as any).providerId;
    if (!providerId) return "Other";

    const normalizedId = String(providerId).toUpperCase();
    return PROVIDER_NAMES[normalizedId] ? PROVIDER_NAMES[normalizedId] : "Other";
  };

  const getProviderIcon = (model: IAiModel): string => {
    const providerId = (model as any).providerId;
    if (!providerId) return "";
    const normalizedId = String(providerId).toUpperCase();
    if (normalizedId === "4D9AC0E3-D3EE-49D1-B0F6-813D9A7C237F") return "openai";
    if (normalizedId === "876905BF-0E2C-4D20-84D1-D2E3B5FAFDA1") return "gemini";
    return "";
  };

  const getModelDropdownMenuOptions = () => {
    // Filter only InstructionModel types and group by provider
    return (
      llmModels
        // .filter((model) => model.typeName === "InstructionModel")
        .filter((model) => model.status === EAiModelStatus.Active)
        .map((model) => {
          const iconName = getProviderIcon(model);
          return {
            key: model.id || model.name,
            label: formatModelLabel(model.name),
            value: model.id || model.name,
            group: getProviderName(model),
            icon: iconName ? (
              <KuikaSvg type="agent-icons" name={iconName} width={12} height={12} useOriginalColors />
            ) : undefined,
            onClick: () => {
              updateModel(model);
            }
          };
        })
    );
  };

  const currentMemoryMode = data.metadata.memoryMode ?? ECrewAiAgentMemoryMode.Disabled;
  const isMemoryEnabled = currentMemoryMode !== ECrewAiAgentMemoryMode.Disabled;
  const getMemoryDropdownMenuOptions = () => [
    {
      key: "memory-disabled",
      label: "Disabled",
      value: String(ECrewAiAgentMemoryMode.Disabled),
      onClick: () => updateMemory(ECrewAiAgentMemoryMode.Disabled)
    },
    {
      key: "memory-shared",
      label: "Shared (crew memory)",
      value: String(ECrewAiAgentMemoryMode.Shared),
      onClick: () => updateMemory(ECrewAiAgentMemoryMode.Shared)
    },
    {
      key: "memory-isolated",
      label: "Isolated (own store)",
      value: String(ECrewAiAgentMemoryMode.Isolated),
      onClick: () => updateMemory(ECrewAiAgentMemoryMode.Isolated)
    }
  ];

  const stopNodePropagation = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
  };

  const getAssignedModelStatus = (): EAiModelStatus | null => {
    const modelRef = data.metadata.model;
    if (!modelRef) return null;
    const modelKey = typeof modelRef === "object" ? (modelRef as any).id || (modelRef as any).name : modelRef;
    const found = llmModels.find((m) => m.id === modelKey || m.name === modelKey);
    if (!found) return null;
    return (found.status as EAiModelStatus) ?? EAiModelStatus.Active;
  };

  const getAppendageGeometry = (
    kind: "model" | "memory" | "tools" | "actions",
    hasValue: boolean,
    spread: number
  ): { offsetX: number; offsetY: number; laneY: number } => {
    // Empty-state geometry is unified so the "+" trigger sits at the same
    // distance from the agent edge regardless of kind.
    const EMPTY_OFFSET_Y = 52;
    const EMPTY_LANE_Y = 36;

    // Legacy Model/Memory branches — they're rendered as in-node meta chips
    // now, not appendages. Kept in case they get restored from
    // docs/ai-agent-top-appendages-restore.md / ai-agent-model-node-circular-restore.md.
    if (kind === "model") {
      return { offsetX: -spread * 0.3, offsetY: EMPTY_OFFSET_Y, laneY: EMPTY_LANE_Y };
    }
    if (kind === "memory") {
      return {
        offsetX: hasValue ? -spread * 0.35 : -spread * 0.15,
        offsetY: hasValue ? 96 : EMPTY_OFFSET_Y,
        laneY: hasValue ? 44 : EMPTY_LANE_Y
      };
    }

    // Actions + Tools are the only appendages left on the canvas. With
    // Model/Memory moved in-node, these two get the full horizontal space to
    // fan out — Actions pulls left, Tools pulls right — and share a single
    // row (vertical stacking is no longer needed at the wider node width).
    if (kind === "actions") {
      return {
        offsetX: hasValue ? -spread * 0.3 : -spread * 0.15,
        offsetY: hasValue ? 50 : EMPTY_OFFSET_Y,
        laneY: hasValue ? 50 : EMPTY_LANE_Y
      };
    }

    // tools
    return {
      offsetX: hasValue ? spread * 0.3 : spread * 0.15,
      offsetY: hasValue ? 110 : EMPTY_OFFSET_Y,
      laneY: hasValue ? 50 : EMPTY_LANE_Y
    };
  };

  // Smooth cubic-Bézier S-curve from the anchor (0,0) down to (offsetX, offsetY).
  // First control point sits directly below the start and second directly above
  // the end, so the line leaves the node vertically and arrives at the target
  // vertically — producing a flowy "root" look instead of a stairstep.
  // `laneY` is kept as a soft hint for the vertical pull-down before the curve
  // starts bending sideways (deeper laneY → more vertical before the bend).
  const renderConnector = (offsetX: number, offsetY: number, laneY: number) => {
    const startX = 0;
    const startY = 0;
    const endX = offsetX;
    const endY = Math.max(offsetY, laneY + 8);

    // Bias control points so the curve exits the node straight down and enters
    // the target straight down. Clamp so tiny offsets still look curvy.
    const pullDown = Math.max(18, Math.min(endY * 0.55, laneY + 10));
    const pullUp = Math.max(10, endY - pullDown);
    const c1x = startX;
    const c1y = pullDown;
    const c2x = endX;
    const c2y = pullUp;

    const padX = 8;
    const padY = 8;
    const minX = Math.min(startX, endX) - padX;
    const maxX = Math.max(startX, endX) + padX;
    const minY = startY;
    const maxY = endY + padY;

    const sx = startX - minX;
    const sy = startY - minY;
    const p1x = c1x - minX;
    const p1y = c1y - minY;
    const p2x = c2x - minX;
    const p2y = c2y - minY;
    const ex = endX - minX;
    const ey = endY - minY;

    const path = `M ${sx} ${sy} C ${p1x} ${p1y}, ${p2x} ${p2y}, ${ex} ${ey}`;

    return (
      <svg
        className="react-flow__node-elk__appendage-connector"
        style={{ left: `${minX}px`, top: `${minY}px`, width: `${maxX - minX}px`, height: `${maxY - minY}px` }}
        viewBox={`0 0 ${maxX - minX} ${maxY - minY}`}
        aria-hidden
      >
        <path d={path} />
      </svg>
    );
  };

  const getToolNodeOffsets = (count: number): Array<{ offsetX: number; offsetY: number }> => {
    if (count <= 0) {
      return [];
    }

    const midpoint = (count - 1) / 2;
    const spreadStep = count > 5 ? 28 : 34;

    return Array.from({ length: count }, (_, index) => {
      const distanceFromCenter = index - midpoint;
      return {
        offsetX: distanceFromCenter * spreadStep,
        offsetY: 22
      };
    });
  };

  // Smooth cubic-Bézier from the tool-list anchor to each individual tool
  // circle. Same root-curve shape as renderConnector so the visual family
  // stays consistent.
  const renderToolNodeConnector = (offsetX: number, offsetY: number, startYOffset: number = 0) => {
    const startX = 0;
    const startY = startYOffset;
    const endX = offsetX;
    const endY = Math.max(offsetY, 14);

    const span = endY - startY;
    const c1x = startX;
    const c1y = startY + span * 0.55;
    const c2x = endX;
    const c2y = startY + span * 0.45;

    const padX = 6;
    const padY = 6;
    const minX = Math.min(startX, endX) - padX;
    const maxX = Math.max(startX, endX) + padX;
    const minY = startY;
    const maxY = endY + padY;

    const sx = startX - minX;
    const sy = startY - minY;
    const p1x = c1x - minX;
    const p1y = c1y - minY;
    const p2x = c2x - minX;
    const p2y = c2y - minY;
    const ex = endX - minX;
    const ey = endY - minY;

    const path = `M ${sx} ${sy} C ${p1x} ${p1y}, ${p2x} ${p2y}, ${ex} ${ey}`;

    return (
      <svg
        className="react-flow__node-elk__appendage-tool-connector"
        style={{ left: `${minX}px`, top: `${minY}px`, width: `${maxX - minX}px`, height: `${maxY - minY}px` }}
        viewBox={`0 0 ${maxX - minX} ${maxY - minY}`}
        aria-hidden
      >
        <path d={path} />
      </svg>
    );
  };

  const renderAppendage = (
    kind: "model" | "memory" | "tools" | "actions",
    label: string,
    options: Array<{
      key: string;
      label: string;
      value: string;
      group?: string;
      icon?: JSX.Element;
      onClick: () => void;
    }>,
    isDisabled: boolean = false,
    anchorLeftPercent: number,
    spread: number,
    value: string = "",
    tools: ICrewAgentTool[] = []
  ) => {
    const hasValue = kind === "tools" || kind === "actions" ? tools.length > 0 : Boolean(value);
    const geometry = getAppendageGeometry(kind, hasValue, spread);
    const targetTransform = `translate(${geometry.offsetX}px, ${geometry.offsetY}px) translateX(-50%)`;

    const triggerControl = (
      <button type="button" className="react-flow__node-elk__appendage-trigger" disabled={isDisabled}>
        <KuikaSvg type="common" name="plus" width={12} height={12} color="var(--dark-6)" />
      </button>
    );

    const selectionControl = (() => {
      if (kind === "model") {
        let statusClass = "";
        const status = getAssignedModelStatus();
        if (status === EAiModelStatus.Shutdown) {
          statusClass = " react-flow__node-elk__appendage-model-node--shutdown";
        } else if (status === EAiModelStatus.Deprecated) {
          statusClass = " react-flow__node-elk__appendage-model-node--deprecated";
        }

        const modelRef = data.metadata.model;
        const modelKey = typeof modelRef === "object" ? (modelRef as any).id || (modelRef as any).name : modelRef;
        const assignedModel = modelKey ? llmModels.find((m) => m.id === modelKey || m.name === modelKey) : undefined;
        const providerIconName = assignedModel ? getProviderIcon(assignedModel) : "";
        const rawModelName = assignedModel?.name || (typeof value === "string" ? value : "");
        const versionBadge = getModelVersionBadge(rawModelName);

        // Dynamic badge placement: short badges (version-only or short size) sit
        // in the top-right corner; wider content centers above the circle so
        // longer text doesn't crowd neighboring appendages.
        const badgeLabelLength = (versionBadge.version?.length || 0) + (versionBadge.size?.length || 0);
        // const badgePlacement =
        //   versionBadge.size && badgeLabelLength > 4 ? "top" : "top-right";
        const badgePlacement = "top-right";
        const badgePlacementClass = ` react-flow__node-elk__appendage-model-node-badge--${badgePlacement}`;

        const nodeButton = (
          <button
            type="button"
            className={`react-flow__node-elk__appendage-model-node${statusClass}`}
            disabled={isDisabled}
          >
            {providerIconName ? (
              <KuikaSvg type="agent-icons" name={providerIconName} width={16} height={16} useOriginalColors />
            ) : (
              <span className="react-flow__node-elk__appendage-model-node-fallback">
                {rawModelName?.charAt(0).toUpperCase() || "?"}
              </span>
            )}
            {(versionBadge.version || versionBadge.size) && (
              <span className={`react-flow__node-elk__appendage-model-node-badge${badgePlacementClass}`}>
                {versionBadge.version && (
                  <span className="react-flow__node-elk__appendage-model-node-badge-version">
                    {versionBadge.version}
                  </span>
                )}
                {versionBadge.size && (
                  <span className="react-flow__node-elk__appendage-model-node-badge-size">{versionBadge.size}</span>
                )}
              </span>
            )}
          </button>
        );

        return (
          <KuikaTooltip content={value || rawModelName || "Model"} position="bottom">
            {nodeButton}
          </KuikaTooltip>
        );
      }

      return (
        <button type="button" className="react-flow__node-elk__appendage-selection" disabled={isDisabled}>
          {truncateSelectionValue(value)}
        </button>
      );
    })();

    const renderToolsNodes = () => {
      if (!tools.length) {
        return isDisabled ? (
          triggerControl
        ) : (
          // kind="tools" - Dropdown for selecting and adding tools to the agent
          <AgentNodeDropdown
            kind={kind}
            options={options}
            placement="bottomRight"
            getPopupContainer={() => document.body}
          >
            {triggerControl}
          </AgentNodeDropdown>
        );
      }

      const showAddButton = !isDisabled && options.length > 0;
      const totalCount = showAddButton ? tools.length + 1 : tools.length;
      const toolOffsets = getToolNodeOffsets(totalCount);

      return (
        <div className="react-flow__node-elk__appendage-tools-list">
          {tools.map((tool, index) => {
            const isKAction = tool.toolType === EAgentToolType.KAction;
            const toolDef = isKAction ? null : getToolDefinition(tool.name);
            const hasMcpConfig = !isKAction && !!toolDef?.mcpConfigName;
            const toolIconName = isKAction ? "ai-action" : toolDef?.icon || "mcp";
            const toolIconType: string = isKAction ? "datasource-icons" : "agent-icons";
            const toolLabel = isKAction ? tool.description || tool.name : toolDef?.displayName || tool.name;
            const offset = toolOffsets[index] || { offsetX: 0, offsetY: 24 };
            const menuKey = `${kind}-${index}`;
            const isMenuOpen = openToolMenuKey === menuKey;

            const menuOptions = [
              {
                key: "delete",
                label: "Delete",
                icon: <KuikaSvg type="common" name="trash" width={14} height={14} />,
                rightSlot: "Del",
                danger: true,
                onClick: () => {
                  setOpenToolMenuKey(null);
                  if (isKAction) {
                    const updated = (kActionTools ?? []).filter((t) => t.actionId !== tool.actionId);
                    updateKActionTools?.(updated);
                  } else {
                    handleToggleTool(tool);
                  }
                }
              }
            ];

            const needsConfig = hasMcpConfig && !tool.settingsId;
            const toolButton = (
              <button
                type="button"
                className={`react-flow__node-elk__appendage-tool-node${needsConfig ? " react-flow__node-elk__appendage-tool-node--needs-config" : ""}`}
                disabled={isDisabled}
                onClick={(e) => {
                  e.stopPropagation();
                  if (!isDisabled) {
                    if (isKAction) {
                      onActionClick?.(tool.actionId);
                    } else if (hasMcpConfig) {
                      // KOA-16736: Pass UNFILTERED index — `tools` here is the regular-tools
                      // subset, but the parent's mcpModalIndexes.toolIndex is used to index
                      // the full data.metadata.tools array (which includes KAction tools).
                      const unfilteredIndex = (data.metadata.tools ?? []).indexOf(tool);
                      onOpenMcpConfig?.(unfilteredIndex >= 0 ? unfilteredIndex : index);
                    } else {
                      onToolClick?.();
                    }
                  }
                }}
              >
                <div style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: "8px" }}>
                  <KuikaSvg
                    type={toolIconType as any}
                    name={toolIconName}
                    width={16}
                    height={16}
                    useOriginalColors={!isKAction && !toolIconName.endsWith("-tool")}
                  />
                </div>
                {needsConfig && (
                  <span className="react-flow__node-elk__appendage-tool-node-warning">
                    <KuikaSvg type="common" name="info-circle" width={14} height={14} color="var(--red-6)" />
                  </span>
                )}
              </button>
            );

            const nodeWithTooltip = (
              <div className={`react-flow__node-elk__appendage-tool-node-wrapper${isMenuOpen ? " menu-open" : ""}`}>
                <KuikaTooltip content={needsConfig ? "Is Not Configured" : toolLabel} position="bottom">
                  {toolButton}
                </KuikaTooltip>
                {!isDisabled && (
                  <KuikaDropdownMenu
                    placement="topLeft"
                    getPopupContainer={() => document.body}
                    closeOnSelect
                    open={isMenuOpen}
                    setOpen={(isOpen) => setOpenToolMenuKey(isOpen ? menuKey : null)}
                    options={menuOptions}
                  >
                    <button
                      type="button"
                      className="react-flow__node-elk__appendage-tool-three-dots"
                      onMouseDown={(e) => e.stopPropagation()}
                    >
                      <KuikaSvg type="common" name="dots" width={14} height={14} color="var(--blue-5)" />
                    </button>
                  </KuikaDropdownMenu>
                )}
              </div>
            );

            const trigger = nodeWithTooltip;

            return (
              <div
                className="react-flow__node-elk__appendage-tool-item"
                key={`${tool.name}-${index}`}
                style={{ left: "50%", top: "25px" }}
              >
                {renderToolNodeConnector(offset.offsetX, offset.offsetY, -25)}
                <div
                  className="react-flow__node-elk__appendage-tool-item-node"
                  style={{ transform: `translate(${offset.offsetX}px, ${offset.offsetY}px) translateX(-50%)` }}
                >
                  {trigger}
                </div>
              </div>
            );
          })}
          {showAddButton &&
            (() => {
              const offset = toolOffsets[tools.length] || { offsetX: 0, offsetY: 24 };
              return (
                <div
                  className="react-flow__node-elk__appendage-tool-item"
                  key="add-tool-btn"
                  style={{ left: "50%", top: "25px" }}
                >
                  {renderToolNodeConnector(offset.offsetX, offset.offsetY, -25)}
                  <div
                    className="react-flow__node-elk__appendage-tool-item-node"
                    style={{ transform: `translate(${offset.offsetX}px, ${offset.offsetY}px) translateX(-50%)` }}
                  >
                    {/* kind="tools" - Add new tools to the agent node */}
                    <AgentNodeDropdown
                      kind={kind}
                      options={options}
                      placement="bottomRight"
                      getPopupContainer={() => document.body}
                    >
                      {triggerControl}
                    </AgentNodeDropdown>
                  </div>
                </div>
              );
            })()}
        </div>
      );
    };

    const isSelectionMode = hasValue;
    const control = isSelectionMode ? selectionControl : triggerControl;
    const showModelLoadingSpinner = kind === "model" && isLoadingModels && hasValue;
    const wrappedControl = showModelLoadingSpinner ? (
      <button type="button" className="react-flow__node-elk__appendage-model-node" disabled>
        <span className="ai-agent-inline-spinner" aria-label="Loading model" />
      </button>
    ) : kind === "tools" || kind === "actions" ? (
      renderToolsNodes()
    ) : isDisabled ? (
      control
    ) : kind === "model" ? (
      // kind="model" - Dropdown for selecting LLM model provider and model type
      <AgentNodeDropdown kind={kind} options={options} placement="bottomRight" getPopupContainer={() => document.body}>
        {control}
      </AgentNodeDropdown>
    ) : (
      // kind="memory" - Dropdown for enabling/disabling agent memory
      <AgentNodeDropdown kind={kind} options={options} placement="bottomRight" getPopupContainer={() => document.body}>
        {control}
      </AgentNodeDropdown>
    );

    return (
      <div
        className={`react-flow__node-elk__appendage react-flow__node-elk__appendage--${kind}`}
        key={label}
        style={{ left: `${anchorLeftPercent}%` }}
        onClick={stopNodePropagation}
        onMouseDown={stopNodePropagation}
      >
        <div className="react-flow__node-elk__appendage-anchor" />
        {renderConnector(geometry.offsetX, geometry.offsetY, geometry.laneY)}
        <span className="react-flow__node-elk__appendage-label">{label}</span>
        <div className="react-flow__node-elk__appendage-target" style={{ transform: targetTransform }}>
          <div className="react-flow__node-elk__appendage-trigger-wrapper">{wrappedControl}</div>
        </div>
      </div>
    );
  };

  const isHierarchical = processType === "hierarchical";
  const hasGuardrail = Boolean(data.metadata.task?.guardrail?.trim());
  const modelDisplayValue = getModelDisplayName(data.metadata.model, llmModels);
  const memoryDisplayValue =
    currentMemoryMode === ECrewAiAgentMemoryMode.Shared
      ? "Shared"
      : currentMemoryMode === ECrewAiAgentMemoryMode.Isolated
        ? "Isolated"
        : "";
  const longValuesCount = [modelDisplayValue, memoryDisplayValue].filter((value) => value.length > 14).length;
  const appendageSpread = 48 + longValuesCount * 10;
  const managerTargetHandlePrefix = `${AI_AGENT_MANAGER_TARGET_HANDLE_PREFIX}-${id}-`;
  const visibleTargetHandles = isHierarchical
    ? data.targetHandles.filter((handle) => handle.id.startsWith(managerTargetHandlePrefix))
    : data.targetHandles;

  const getTargetHandleGeometry = (
    handleId: string
  ): { position: Position; leftPercent: number; topPercent: number } => {
    if (!isHierarchical || !handleId.startsWith(managerTargetHandlePrefix)) {
      return { position: Position.Left, leftPercent: 2, topPercent: 50 };
    }

    const rawPayload = handleId.slice(managerTargetHandlePrefix.length);
    const [rawPosition, rawLeftPercent, rawTopPercent] = rawPayload.split("::");
    const leftPercent = Number(rawLeftPercent);
    const topPercent = Number(rawTopPercent);

    const position =
      rawPosition === "right"
        ? Position.Right
        : rawPosition === "top"
          ? Position.Top
          : rawPosition === "bottom"
            ? Position.Bottom
            : Position.Left;

    return {
      position,
      leftPercent: Number.isFinite(leftPercent) ? leftPercent : 2,
      topPercent: Number.isFinite(topPercent) ? topPercent : 50
    };
  };

  const getTargetHandlePosition = (handleId: string): Position => {
    return getTargetHandleGeometry(handleId).position;
  };

  const getTargetHandleStyle = (handleId: string): React.CSSProperties | undefined => {
    if (!isHierarchical || !handleId.startsWith(managerTargetHandlePrefix)) {
      return undefined;
    }

    const geometry = getTargetHandleGeometry(handleId);
    return {
      position: "absolute",
      left: `${geometry.leftPercent}%`,
      top: `${geometry.topPercent}%`,
      transform: "translate(-50%, -50%)"
    };
  };

  return (
    <>
      <div className={`handles targets ${isHierarchical ? "targets--planetary" : ""}`}>
        {visibleTargetHandles.map((handle) => (
          <Handle
            key={handle.id}
            id={handle.id}
            type="target"
            position={getTargetHandlePosition(handle.id)}
            style={getTargetHandleStyle(handle.id)}
          />
        ))}
      </div>
      <div className={`react-flow__node-elk__node-container ${data.isAnySelected && !selected ? "dimmed" : ""}`}>
        {selected && !readOnly && (
          <div
            className="react-flow__node-elk__header-actions"
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
          >
            <KuikaDropdownMenu
              placement="topLeft"
              getPopupContainer={() => document.body}
              closeOnSelect
              open={contextMenuOpen}
              setOpen={setContextMenuOpen}
              options={[
                {
                  key: "rename",
                  label: "Rename",
                  icon: <KuikaSvg type="common" name="edit" width={14} height={14} />,
                  onClick: () => setIsNameOnEdit(true)
                },
                {
                  key: "copy",
                  label: "Copy",
                  icon: <KuikaSvg type="common" name="copy" width={14} height={14} />,
                  rightSlot: "Cmd + C",
                  onClick: onCopyNode,
                  showDividerAfter: true
                },
                {
                  key: "delete",
                  label: "Delete",
                  icon: <KuikaSvg type="common" name="trash" width={14} height={14} />,
                  rightSlot: "Del",
                  danger: true,
                  onClick: onRemoveNode
                }
              ]}
            >
              <button type="button" className="react-flow__node-elk__three-dots">
                <KuikaSvg type="common" name="dots" width={16} height={16} color="var(--blue-5)" />
              </button>
            </KuikaDropdownMenu>
          </div>
        )}
        <div className="react-flow__node-elk__header">
          <div className="react-flow__node-elk__header-row">{agentNodeTitleWrapper(data.metadata.name)}</div>
          {renderAgentMeta()}
        </div>
        <div className="react-flow__node-elk__appendages">
          {renderAppendage(
            "actions",
            "Actions",
            getActionsDropdownMenuOptions(),
            readOnly || (kActionOptions ?? []).length === 0,
            25,
            appendageSpread,
            "",
            kActionTools ?? []
          )}
          {renderAppendage(
            "tools",
            "Tools",
            getToolsDropdownMenuOptions(),
            readOnly || isNoToolAvailable(),
            75,
            appendageSpread,
            "",
            // KOA-16736: exclude KAction tools — those render in the Actions appendage
            (data.metadata.tools ?? []).filter((t) => t.toolType !== EAgentToolType.KAction)
          )}
        </div>
      </div>
      {!isHierarchical && (
        <div className="handles sources">
          {data.sourceHandles.length > 0 ? (
            data.sourceHandles.map((handle) => (
              // right now we can only add agent, ignore the drag event
              <KuikaTooltip key={handle.id} content={readOnly ? "" : "Click to add agent"} position="right">
                <Handle
                  id={handle.id}
                  type="source"
                  position={Position.Right}
                  onClick={(e) => {
                    if (!readOnly) {
                      e.stopPropagation();
                      onAddAgentAfter();
                    }
                  }}
                  style={{ cursor: readOnly ? "default" : "pointer" }}
                />
              </KuikaTooltip>
            ))
          ) : (
            <KuikaTooltip content={readOnly ? "" : "Click to add agent"} position="right">
              <Handle
                id="default-source"
                type="source"
                position={Position.Right}
                onClick={(e) => {
                  if (!readOnly) {
                    e.stopPropagation();
                    onAddAgentAfter();
                  }
                }}
                style={{ cursor: readOnly ? "default" : "pointer" }}
              />
            </KuikaTooltip>
          )}
        </div>
      )}
    </>
  );
}
